import { useState } from "react";
import { Link } from "react-router-dom";
import { WorkCard } from "../components/WorkCard";
import { useLibrary } from "../contexts/LibraryContext";
import { usePageMeta } from "../hooks/usePageMeta";
import { assignArtwork, browseArtwork } from "../artwork";

export function LibraryPage() {
  const library = useLibrary();
  const [editing, setEditing] = useState("");
  const [collectionName, setCollectionName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  usePageMeta(
    "Saved",
    "Saved works, listening history, progress, and completed items stored on this device.",
    "/saved",
  );
  const favorites = Object.values(library.favorites);
  const collectionWorks = [
    ...new Map(
      [
        ...favorites,
        ...Object.values(library.collections).flatMap((c) => c.works || []),
      ].map((w) => [w.id, w]),
    ).values(),
  ];
  const inProgress = library.history.filter(
    (work) =>
      (library.progress[work.id] || 0) > 0 &&
      !library.completed.includes(work.id),
  );
  const savedSeries = Object.values(library.savedSeries);
  const visibleWorks = [
    ...favorites,
    ...inProgress,
    ...library.history.slice(0, 20),
  ];
  const artwork = assignArtwork(visibleWorks, "saved");
  let artworkOffset = 0;
  const nextArtwork = (count: number) =>
    artwork.slice(artworkOffset, (artworkOffset += count));
  const favoriteArtwork = nextArtwork(favorites.length);
  const inProgressArtwork = nextArtwork(inProgress.length);
  const historyArtwork = nextArtwork(library.history.slice(0, 20).length);
  return (
    <div className="library-page">
      <header className="page-heading">
        <div className="section-kicker">YOUR OWN LITTLE LIBRARY</div>
        <h1>Saved</h1>
        <p>
          Keep the words you want to return to. Your saves and listening
          progress stay with you on this device.
        </p>
      </header>
      <div className="library-stats">
        <div>
          <strong>{favorites.length + savedSeries.length}</strong>
          <span>Saved</span>
        </div>
        <div>
          <strong>{inProgress.length}</strong>
          <span>In progress</span>
        </div>
        <div>
          <strong>{library.completed.length}</strong>
          <span>Completed</span>
        </div>
      </div>
      <section>
        <h2>Your collections</h2>
        <form
          className="collection-create"
          onSubmit={(e) => {
            e.preventDefault();
            if (collectionName.trim()) {
              library.saveCollection(
                editing || crypto.randomUUID(),
                collectionName.trim(),
                selected,
              );
              setCollectionName("");
              setEditing("");
              setSelected([]);
            }
          }}
        >
          <label>
            Collection name
            <input
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              placeholder="Sunday listening"
              required
            />
          </label>
          <fieldset>
            <legend>Choose saved works</legend>
            {collectionWorks.map((w) => (
              <label key={w.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(w.id)}
                  onChange={(e) =>
                    setSelected(
                      e.target.checked
                        ? [...selected, w.id]
                        : selected.filter((id) => id !== w.id),
                    )
                  }
                />
                {w.title}
              </label>
            ))}
          </fieldset>
          <button className="secondary-action">
            {editing ? "Save collection" : "Create collection"}
          </button>
          {editing ? (
            <button
              type="button"
              onClick={() => {
                setEditing("");
                setCollectionName("");
                setSelected([]);
              }}
            >
              Cancel
            </button>
          ) : null}
        </form>
        {Object.entries(library.collections).map(([id, c]) => (
          <details className="saved-collection" key={id}>
            <summary>
              {c.name} · {c.workIds.length} works
            </summary>
            <div className="saved-collection-actions">
              <button
                onClick={() => {
                  setEditing(id);
                  setCollectionName(c.name);
                  setSelected(c.workIds);
                  document
                    .querySelector(".collection-create")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Edit collection
              </button>
              <button onClick={() => library.saveCollection(id, "", [])}>
                Delete collection
              </button>
            </div>
            {c.workIds
              .map(
                (workId) =>
                  library.favorites[workId] ||
                  library.history.find((w) => w.id === workId) ||
                  c.works?.find((w) => w.id === workId),
              )
              .filter(Boolean)
              .map((w) => (
                <WorkCard key={w!.id} work={w!} compact />
              ))}
          </details>
        ))}
      </section>
      {savedSeries.length ? (
        <section>
          <h2>Saved series</h2>
          <div className="saved-series-list">
            {savedSeries.map((series) => (
              <Link to={`/series/${series.slug}`} key={series.id}>
                <img
                  src={series.artworkUrl || browseArtwork(series.id)}
                  alt=""
                  aria-hidden="true"
                />
                <span>
                  <b>{series.title}</b>
                  <small>{series.description}</small>
                </span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
      <section>
        <h2>Saved works</h2>
        {favorites.length ? (
          <div className="library-list">
            {favorites.map((work, index) => (
              <WorkCard
                key={work.id}
                work={work}
                compact
                artwork={favoriteArtwork[index]}
              />
            ))}
          </div>
        ) : (
          <div className="empty-library">
            <span>◇</span>
            <h3>Your shelf is ready</h3>
            <p>
              Save a teaching, book, or piece of music and it will appear here.
            </p>
          </div>
        )}
      </section>
      {inProgress.length ? (
        <section>
          <h2>Continue listening</h2>
          <div className="library-list">
            {inProgress.map((work, index) => (
              <WorkCard
                key={work.id}
                work={work}
                compact
                artwork={inProgressArtwork[index]}
                reason={`${Math.floor(library.progress[work.id] / 60)} minutes played`}
              />
            ))}
          </div>
        </section>
      ) : null}
      {library.history.length ? (
        <section>
          <h2>Recent history</h2>
          <div className="library-list">
            {library.history.slice(0, 20).map((work, index) => (
              <WorkCard
                key={work.id}
                work={work}
                compact
                artwork={historyArtwork[index]}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
